import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaBolt,
  FaCheckCircle,
  FaCrown,
  FaGem,
  FaSignInAlt,
  FaTag,
  FaTimes,
} from "react-icons/fa";
import {
  getPlans,
  createInvoice,
  previewDiscount,
  checkDiscount,
} from "../../api/payments";
import { useAuth } from "../../contexts/AuthContext";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Spinner } from "../../ui";
import {
  clearPendingDiscount,
  getPendingDiscount,
  normalizeCode,
  savePendingDiscount,
} from "../../utils/pendingDiscount";
import { currencyLabel, formatMoney } from "../../utils/money";

// Our non-recurring 3m / 6m / 12m plans.
const PLAN_META = {
  "3m": { label: "3 أشهر", Icon: FaBolt },
  "6m": { label: "6 أشهر", Icon: FaGem },
  "12m": { label: "سنة كاملة", Icon: FaCrown },
};

const urlParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch (_) {
    return "";
  }
};

// The code from the link wins; otherwise the one the visitor brought earlier.
const initialCode = () => normalizeCode(urlParam("code")) || getPendingDiscount();

/**
 * The plans, the discount code and the pay button. Works signed out too: a
 * code is then priced by the public check (rules about the code only) and
 * the button asks the visitor to sign in, coming back here with the code and
 * the chosen plan. Signed in, the account-aware preview takes over, and the
 * server re-checks everything when the invoice is created.
 */
const PlanCards = ({ source = "pricing_page", onPurchaseStart }) => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const signedIn = isAuthenticated;

  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => urlParam("plan") || null);
  const viewedRef = useRef(false);

  // Discount code: what the user typed, and what the server said it is worth.
  const [codeInput, setCodeInput] = useState(initialCode);
  const [codeOpen, setCodeOpen] = useState(() => !!initialCode());
  const [discount, setDiscount] = useState(null); // valid preview
  const [codeError, setCodeError] = useState("");
  const [checking, setChecking] = useState(false);

  const applyCode = async (raw) => {
    const code = normalizeCode(raw);
    if (!code || checking) return;
    setChecking(true);
    setCodeError("");
    try {
      const res = signedIn ? await previewDiscount(code) : await checkDiscount(code);
      if (res.valid) {
        setDiscount({ ...res, code: res.code || code, checkedSignedIn: signedIn });
        savePendingDiscount(res.code || code);
        queueEvent(EVENTS.PAYWALL_DISCOUNT_APPLIED, {
          code: res.code || code,
          source,
          signed_in: signedIn,
        });
      } else {
        setDiscount(null);
        setCodeError(res.message || "كود الخصم غير صحيح");
        // Don't bring a dead code back every time the plans are shown.
        clearPendingDiscount();
      }
    } catch (e) {
      setDiscount(null);
      if (!signedIn && e?.response?.status === 404) {
        // Public check not available: keep the code, it's checked after login.
        savePendingDiscount(code);
        setDiscount({ code, plans: [], needsAccountCheck: true, unchecked: true });
      } else {
        setCodeError(
          e?.response?.status === 429
            ? "محاولات كثيرة، انتظر قليلاً ثم حاول"
            : "تعذّر التحقق من الكود"
        );
      }
    } finally {
      setChecking(false);
    }
  };

  const clearCode = () => {
    setDiscount(null);
    setCodeInput("");
    setCodeError("");
    clearPendingDiscount();
  };

  // A code from the link is checked once we know who is asking, and again
  // after signing in (the account may change the answer).
  useEffect(() => {
    if (authLoading) return;
    if (codeInput) applyCode(codeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, authLoading]);

  const { data, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
  });

  const plans = data?.plans || [];
  const currency = data?.currency || "";
  const configured = data?.configured;
  const dec = currency === "KWD" ? 3 : 2;

  const discountOf = (plan) => {
    const row = discount?.plans?.find((x) => x.plan === plan);
    return row && row.applies ? row : null;
  };

  const purchasable = plans.filter((p) => p.price > 0);
  // Default to the longest (best-value) purchasable plan.
  const effectiveSelected =
    (selected && purchasable.some((p) => p.plan === selected) && selected) ||
    purchasable[purchasable.length - 1]?.plan ||
    null;

  // "وفر %" is computed against the shortest plan's monthly rate.
  const baseline = purchasable[0];
  const baseMonthly = baseline ? baseline.price / baseline.months : 0;
  const savingsPct = (p) => {
    if (!baseMonthly || !p.price) return 0;
    const full = baseMonthly * p.months;
    return Math.round(((full - p.price) / full) * 100);
  };

  // paywall_viewed once the purchasable plans actually rendered (matches the
  // app, which fires it after the RevenueCat offerings load).
  useEffect(() => {
    if (viewedRef.current || isLoading || !configured || plans.length === 0) {
      return;
    }
    viewedRef.current = true;
    queueEvent(EVENTS.PAYWALL_VIEWED, {
      source,
      packageTypes: purchasable.map((p) => p.plan).join(","),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, configured, plans.length]);

  // Signed out: sign in (or sign up) and come back here with the same code
  // and plan.
  const signInToSubscribe = () => {
    const params = new URLSearchParams();
    if (discount?.code) params.set("code", discount.code);
    if (effectiveSelected) params.set("plan", effectiveSelected);
    const qs = params.toString();
    navigate("/login", { state: { from: `/pricing${qs ? `?${qs}` : ""}` } });
  };

  const subscribe = async () => {
    if (!effectiveSelected) return;
    if (!signedIn) {
      signInToSubscribe();
      return;
    }
    setBusy(true);
    queueEvent(EVENTS.PAYWALL_PURCHASE_STARTED, {
      plan: effectiveSelected,
      method: "myfatoorah",
      source,
    });
    onPurchaseStart?.(effectiveSelected);
    try {
      // Only send the code for a plan it applies to; the server recomputes
      // the amount either way.
      const code = discountOf(effectiveSelected) ? discount.code : undefined;
      const { invoiceId, paymentUrl } = await createInvoice(
        effectiveSelected,
        code
      );
      localStorage.setItem("mf_invoice", invoiceId);
      // Read back by /checkout/callback so the purchase outcome events carry
      // the plan and the CTA source that started the funnel.
      localStorage.setItem("mf_plan", effectiveSelected);
      localStorage.setItem("mf_source", source);
      window.location.href = paymentUrl;
    } catch (e) {
      queueEvent(EVENTS.PAYWALL_PURCHASE_FAILED, {
        reason: e?.response?.data?.message || e?.message || "invoice_failed",
        plan: effectiveSelected,
        source,
      });
      // The code stopped being valid between preview and checkout.
      if (e?.response?.data?.code === "DISCOUNT_INVALID") {
        setDiscount(null);
        setCodeError(e.response.data.message || "كود الخصم لم يعد صالحاً");
      }
      // Only our own Arabic messages are shown; anything else (gateway or
      // server internals, timeouts) gets the plain fallback.
      const serverMessage = e?.response?.data?.message;
      toast.error(
        typeof serverMessage === "string" && /[؀-ۿ]/.test(serverMessage)
          ? serverMessage
          : "تعذّر بدء الدفع، حاول مرة أخرى"
      );
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12 text-white">
        <Spinner size={28} />
      </div>
    );
  }
  if (!configured) {
    return (
      <p className="text-center text-white/80 bg-white/10 rounded-2xl p-6">
        الدفع غير متاح حالياً. حاول لاحقاً.
      </p>
    );
  }

  const selectedDiscount = effectiveSelected && discountOf(effectiveSelected);

  return (
    <div dir="rtl">
      <div className="space-y-3 mb-5" role="radiogroup" aria-label="الباقات">
        {plans.map((p) => {
          const meta = PLAN_META[p.plan] || {
            label: `${p.months} أشهر`,
            Icon: FaGem,
          };
          const { Icon } = meta;
          const hasPrice = p.price > 0;
          const isSel = effectiveSelected === p.plan;
          const sv = savingsPct(p);
          const off = discountOf(p.plan);
          const finalPrice = off ? off.finalAmount : p.price;
          const perMonth = hasPrice ? finalPrice / p.months : 0;
          return (
            <button
              key={p.plan}
              type="button"
              role="radio"
              aria-checked={isSel}
              disabled={!hasPrice}
              onClick={() => {
                setSelected(p.plan);
                queueEvent(EVENTS.PAYWALL_PLAN_SELECTED, {
                  plan: p.plan,
                  source,
                });
              }}
              className={`relative w-full rounded-2xl text-right p-4 flex items-center gap-3 border-2 transition-colors ${
                isSel
                  ? "border-mainButton bg-mainButton/15"
                  : "border-white/10 bg-white/[0.05] hover:border-white/25"
              } ${hasPrice ? "" : "opacity-50 cursor-not-allowed"}`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  isSel ? "border-mainButton" : "border-white/40"
                }`}
                aria-hidden="true"
              >
                {isSel && <span className="w-2.5 h-2.5 rounded-full bg-mainButton" />}
              </span>
              <span
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                  isSel ? "bg-mainButton text-white" : "bg-white/10 text-primary"
                }`}
                aria-hidden="true"
              >
                <Icon />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold">{meta.label}</div>
                <div className="text-white/60 text-xs">
                  {hasPrice
                    ? `≈ ${formatMoney(perMonth, currency, dec)} / شهر`
                    : "قريباً"}
                </div>
              </div>
              {hasPrice && (
                <div className="text-left">
                  {off && (
                    <div className="text-white/50 text-xs line-through whitespace-nowrap">
                      {formatMoney(p.price, currency)}
                    </div>
                  )}
                  <div
                    className={`font-extrabold text-lg whitespace-nowrap ${
                      off ? "text-success" : "text-white"
                    }`}
                  >
                    {finalPrice}{" "}
                    <span className="text-xs font-medium text-white/70">
                      {currencyLabel(currency)}
                    </span>
                  </div>
                </div>
              )}
              {hasPrice && sv > 0 && (
                <span className="absolute -top-2.5 left-4 bg-success text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow">
                  وفّر {sv}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Discount code */}
      <div className="mb-5">
        {discount ? (
          <div className="flex items-center justify-between gap-2 rounded-xl border border-success/40 bg-success/10 px-3.5 py-3">
            <span className="text-white text-sm flex items-start gap-2.5">
              <FaCheckCircle className="text-success mt-0.5 shrink-0" />
              <span>
                {discount.unchecked ? "تم حفظ الكود" : "تم تطبيق الكود"}{" "}
                <bdi dir="ltr" className="font-extrabold">
                  {discount.code}
                </bdi>
                {effectiveSelected &&
                  discount.plans?.length > 0 &&
                  !selectedDiscount && (
                    <span className="block text-white/70 text-xs mt-0.5">
                      لا ينطبق على الباقة المختارة
                    </span>
                  )}
                {!signedIn && discount.needsAccountCheck && (
                  <span className="block text-white/70 text-xs mt-0.5">
                    نتأكد أنه متاح لحسابك بعد تسجيل الدخول
                  </span>
                )}
              </span>
            </span>
            <button
              type="button"
              onClick={clearCode}
              className="text-white/60 hover:text-white p-1.5 rounded-md hover:bg-white/10"
              aria-label="إزالة كود الخصم"
            >
              <FaTimes />
            </button>
          </div>
        ) : codeOpen ? (
          <div>
            <label htmlFor="discount-code" className="block text-sm font-semibold text-white/90 mb-1.5">
              كود الخصم
            </label>
            <div className="flex gap-2">
              <input
                id="discount-code"
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase());
                  setCodeError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && applyCode(codeInput)}
                placeholder="SAVE20"
                maxLength={40}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                dir="ltr"
                aria-invalid={!!codeError || undefined}
                className={`flex-1 min-w-0 h-12 rounded-xl bg-white/[0.07] border text-white text-center font-bold tracking-widest placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                  codeError ? "border-danger" : "border-white/15 focus:border-primary"
                }`}
              />
              <Button
                variant="secondary"
                onClick={() => applyCode(codeInput)}
                disabled={!codeInput.trim()}
                loading={checking}
              >
                تطبيق
              </Button>
            </div>
            {codeError && (
              <p className="text-danger text-sm mt-2 font-medium" role="alert">
                {codeError}
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCodeOpen(true)}
            className="text-primary text-sm font-bold flex items-center gap-2 mx-auto hover:text-mainText"
          >
            <FaTag /> عندك كود خصم؟
          </button>
        )}
      </div>

      {signedIn || authLoading ? (
        <Button
          size="lg"
          block
          onClick={subscribe}
          disabled={!effectiveSelected || authLoading}
          loading={busy}
          iconEnd={<FaArrowLeft className="text-base" />}
        >
          اشترك الآن
        </Button>
      ) : (
        <>
          <Button
            size="lg"
            block
            onClick={subscribe}
            disabled={!effectiveSelected}
            icon={<FaSignInAlt className="text-base" />}
          >
            سجّل الدخول للاشتراك
          </Button>
          <p className="text-center text-white/60 text-xs mt-3 leading-relaxed">
            الاشتراك يُضاف لحسابك. ما عندك حساب؟ تقدر تنشئ واحد من صفحة الدخول.
          </p>
        </>
      )}
    </div>
  );
};

export default PlanCards;
